import { getFilenameFromUrl } from "helpers/url";
import { orientationByAspectRatio } from "model/creative/helpers/orientationByAspectRatio";
import { MREC_DICTIONARY_ITEMS } from "redux/dictionary/useAspectRatioDictionary";
import { CreativeChangeDetail } from "./creativeCustomCases";
import { ChangeHistoryAction } from "./types";
import { CreativeSnapshot } from "./useCreativeSnapshot";

const ASSET_PATH = /^\/creative\/assets\/(\d+)\/(image|data|title)\/(\w+)$/;
const ASPECT_RATIO_PATH = /^\/creative\/aspectRatios\/values$/;

const DATA_TYPE_LABELS: Record<string, string> = {
  DESC: "Creative caption",
  CTATEXT: "CTA button text",
  SPONSORED: "Sponsored",
  RATING: "Rating",
};

const IMAGE_TYPE_LABELS: Record<string, string> = {
  MAIN: "Main image",
  ICON: "App icon",
};

const TITLE_LABEL = "App title";

// the order the edit form lays the fields out, which is also the order they go into
// the assets array
const ASSET_KEY_ORDER = [
  "image:MAIN",
  "image:ICON",
  "title",
  "data:DESC",
  "data:CTATEXT",
  "data:SPONSORED",
  "data:RATING",
];

export const isAssetPath = (fieldPath: string) => ASSET_PATH.test(fieldPath);

export const isAspectRatioPath = (fieldPath: string) =>
  ASPECT_RATIO_PATH.test(fieldPath);

/**
 * The change row only carries the position an asset sat at, which means nothing on
 * its own since assets only enter the array when they have a value. The snapshot is
 * the creative as it was at that moment, so the position can finally be decoded.
 */
export const getAssetFieldName = (
  creative: CreativeSnapshot | null,
  fieldPath: string
): string | undefined => {
  const match = fieldPath.match(ASSET_PATH);
  if (!match || !creative?.assets) return undefined;

  const [, index, kind, leaf] = match;
  const asset = creative.assets[Number(index)] as any;

  if (!asset) return undefined;

  if (kind === "title" && asset.title) return TITLE_LABEL;

  if (kind === "data" && asset.data) return DATA_TYPE_LABELS[asset.data.type];

  if (kind === "image" && asset.image) {
    const label = IMAGE_TYPE_LABELS[asset.image.type];
    return label ? `${label} (${leaf})` : undefined;
  }

  return undefined;
};

type NamedAsset = { key: string; label: string; value: string };

export type AssetChange = {
  action: ChangeHistoryAction;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
};

const namedAsset = (asset: any): NamedAsset | undefined => {
  if (asset?.title) {
    return { key: "title", label: TITLE_LABEL, value: asset.title.text ?? "" };
  }

  if (asset?.data) {
    const label = DATA_TYPE_LABELS[asset.data.type];
    return label
      ? {
          key: `data:${asset.data.type}`,
          label,
          value: asset.data.value ?? "",
        }
      : undefined;
  }

  if (asset?.image) {
    const label = IMAGE_TYPE_LABELS[asset.image.type];
    return label
      ? {
          key: `image:${asset.image.type}`,
          label,
          // a re-upload always lands on a new url, so the filename is the change
          value: asset.image.url ? getFilenameFromUrl(asset.image.url) : "",
        }
      : undefined;
  }

  return undefined;
};

const namedAssets = (creative: CreativeSnapshot | null) => {
  const byKey = new Map<string, NamedAsset>();

  (creative?.assets ?? []).forEach((asset) => {
    const named = namedAsset(asset);
    if (named) byKey.set(named.key, named);
  });

  return byKey;
};

const orderOf = (key: string) => {
  const index = ASSET_KEY_ORDER.indexOf(key);
  return index === -1 ? ASSET_KEY_ORDER.length : index;
};

/**
 * A native creative's fields live in one positional array, so a single edit can move
 * every asset after it and the change rows end up describing index shifts rather than
 * fields. Comparing the two documents by what each asset *is* gives one entry per
 * field the user actually touched.
 */
export const getAssetChanges = (
  before: CreativeSnapshot | null,
  after: CreativeSnapshot | null
): AssetChange[] => {
  const beforeAssets = namedAssets(before);
  const afterAssets = namedAssets(after);

  const keys = [...new Set([...beforeAssets.keys(), ...afterAssets.keys()])];

  return keys
    .sort((a, b) => orderOf(a) - orderOf(b))
    .reduce<AssetChange[]>((changes, key) => {
      const old = beforeAssets.get(key);
      const now = afterAssets.get(key);

      if (!old && now) {
        changes.push({
          action: "added",
          fieldName: now.label,
          newValue: now.value,
        });
      } else if (old && !now) {
        changes.push({
          action: "removed",
          fieldName: old.label,
          oldValue: old.value,
        });
      } else if (old && now && old.value !== now.value) {
        changes.push({
          action: "updated",
          fieldName: now.label,
          oldValue: old.value,
          newValue: now.value,
        });
      }

      return changes;
    }, []);
};

const orientationName = (values?: string[]) =>
  values?.length ? orientationByAspectRatio({ values })?.name : undefined;

const placementType = (values?: string[]) => {
  if (!values?.length) return undefined;
  return MREC_DICTIONARY_ITEMS.some((ratio) => values.includes(ratio.id))
    ? "Medium rectangle"
    : "Fullscreen";
};

/**
 * Both are worked out from the whole ratio list rather than stored, so they need the
 * two documents; the change row only says which ratios came and went.
 */
export const getAspectRatioDetails = (
  before: CreativeSnapshot | null,
  after: CreativeSnapshot | null
): CreativeChangeDetail[] => {
  if (!before || !after) return [];

  const beforeValues = before.aspectRatios?.values;
  const afterValues = after.aspectRatios?.values;

  return [
    {
      label: "Orientation",
      oldValue: orientationName(beforeValues),
      newValue: orientationName(afterValues),
    },
    {
      label: "Placement type",
      oldValue: placementType(beforeValues),
      newValue: placementType(afterValues),
    },
  ].filter((d) => d.oldValue || d.newValue);
};
